import { useMemo } from "react";

import { mergedFillPath, mergedLabelPoint, mergedOutlineSegments, mergedRoomBridges } from "./mergedRooms";
import type { FloorplanRoom } from "./types";

function formatRelative(isoDate: string): string {
  const d = new Date(isoDate);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const days = Math.round(diffMs / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  if (days > 0 && days <= 30) return `in ${days}d`;
  if (days < 0 && days >= -30) return `${-days}d ago`;
  return isoDate;  // fallback to date string
}

interface FloorplanRoomOverlayProps {
  rooms: FloorplanRoom[];
  selectedRoomId?: number | null;
  onRoomClick?: (room: FloorplanRoom) => void;
  /** Show management-only info (ending dates, upcoming tenants) */
  showManagementDetails?: boolean;
  /**
   * Render tenant names in room labels. Driven by the `tenant_names` layer.
   * Off, an occupied office falls back to its description/room number, and
   * status lines drop to their name-free forms. Meeting rooms are unaffected:
   * with no occupancies their label already resolves to the room name.
   *
   * Defaults to true so a caller that predates the layer — or a site whose
   * registry has no `tenant_names` row — keeps today's behaviour instead of
   * silently blanking every occupied room.
   */
  showTenantNames?: boolean;
  /** #428: rooms to draw with a dashed stroke alongside the selection (a lease's other rooms, or a
   *  portal member's own rooms). Unset = unchanged rendering (R17). */
  relatedRoomIds?: number[];
}

function polygonCentroid(points: number[][]): [number, number] {
  if (points.length === 0) return [0, 0];

  let area = 0;
  let cx = 0;
  let cy = 0;

  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    const cross = points[i][0] * points[j][1] - points[j][0] * points[i][1];
    area += cross;
    cx += (points[i][0] + points[j][0]) * cross;
    cy += (points[i][1] + points[j][1]) * cross;
  }

  area /= 2;
  if (Math.abs(area) < 1e-10) {
    // Degenerate polygon — fall back to simple average
    const avgX = points.reduce((s, p) => s + p[0], 0) / points.length;
    const avgY = points.reduce((s, p) => s + p[1], 0) / points.length;
    return [avgX, avgY];
  }

  cx /= 6 * area;
  cy /= 6 * area;
  return [cx, cy];
}

export function getRoomColor(room: FloorplanRoom, mgmt: boolean): string {
  // Bookable rooms: availability takes priority
  if (room.isAvailable === true) return "#22c55e"; // green — available
  if (room.isAvailable === false) return "#ef4444"; // red — booked

  // Tenant-assignable rooms: occupancy status
  if (room.isTenantAssignable) {
    if (room.occupancyStatus === "occupied") {
      if (room.isNewNeighbour || room.isNewMember) return "#06b6d4"; // cyan — new
      return "#3b82f6"; // blue — occupied
    }
    if (room.occupancyStatus === "ending") {
      if (!mgmt) return "#22c55e"; // member portal: just show as occupied
      if (room.upcomingOccupancies?.length) return "#14b8a6"; // teal — transition
      return "#f59e0b"; // amber — leaving, no replacement
    }
    if (room.occupancyStatus === "upcoming") return "#c4b5fd"; // lavender — future move-in (distinct from empty gray + occupied blue; no identity surfaced on public floorplan)
    return "#64748b"; // gray — empty, no occupancy
  }

  if (room.isBookable === false) return "#64748b"; // gray — non-assignable, non-bookable
  // Bookable but no availability data (calendar not connected)
  if (room.isBookable && room.isAvailable == null) return "#a78bfa"; // purple — bookable, not connected
  return "#64748b"; // fallback
}

function formatMinutes(m: number): string {
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r > 0 ? `${h}h ${r}m` : `${h}h`;
}

function formatSlotTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

// Each occupancy's tenantName may itself contain "\n" when the tenant
// has multiple display_names (the backend joins them with "\n" so
// multi-line consumers can split them apart). Flatten across both axes
// so each individual name lands on its own line on the plan.
function tenantNameLines(room: FloorplanRoom): string[] {
  return (room.occupancies ?? [])
    .flatMap(o => (o.tenantName ?? "").split("\n"))
    .map(s => s.trim())
    .filter(Boolean);
}

// Compact joined form for inline use (status sentences like
// "ClearMinds, Nodus → 12 days"). Multi-line rendering uses
// ``tenantNameLines`` directly.
function allTenantNames(room: FloorplanRoom): string {
  return tenantNameLines(room).join(", ");
}

// ``showTenantNames`` gates the name, not the status. Every branch below
// already has a name-free form for the case where a room is occupied but no
// name resolved ("Occupied", "Ending <date>", "Upcoming"), so hiding names is
// just forcing that path — otherwise the tenant would stay perfectly readable
// in the status line under a label that had supposedly hidden it.
function getStatusLines(room: FloorplanRoom, mgmt: boolean, showTenantNames = true): string[] {
  if (room.isBookable) {
    if (room.isAvailable === true) {
      const lines: string[] = [];
      if (room.nextFreeSlotDurationMinutes != null) {
        const d = room.nextFreeSlotDurationMinutes;
        lines.push(d < 20 ? `Free ${formatMinutes(d)} only` : `Free for ${formatMinutes(d)}`);
      } else {
        lines.push("Available");
      }
      return lines;
    }
    if (room.isAvailable === false) {
      const lines: string[] = ["Booked"];
      if (room.nextFreeSlotStart) {
        const minsUntil = Math.max(0, Math.round((new Date(room.nextFreeSlotStart).getTime() - Date.now()) / 60000));
        lines.push(`Free in ${formatMinutes(minsUntil)}`);
        if (room.nextFreeSlotDurationMinutes != null) {
          const end = new Date(new Date(room.nextFreeSlotStart).getTime() + room.nextFreeSlotDurationMinutes * 60000);
          lines.push(`Free slot: ${formatSlotTime(room.nextFreeSlotStart)}–${formatSlotTime(end.toISOString())}`);
        }
      }
      return lines;
    }
    if (room.isAvailable == null) return [];
    return [];
  }
  // Occupancy status for tenant-assignable rooms
  if (room.isTenantAssignable) {
    if (room.occupancyStatus === "occupied") {
      return [(showTenantNames && allTenantNames(room)) || "Occupied"];
    }
    if (room.occupancyStatus === "ending") {
      const name = showTenantNames ? allTenantNames(room) : "";
      if (!mgmt) return [name || "Occupied"];
      const endDate = room.occupancyEndDate;
      const relative = endDate ? formatRelative(endDate) : "";
      return [name ? `${name} → ${relative}` : `Ending ${relative}`];
    }
    if (room.occupancyStatus === "upcoming") {
      if (!mgmt) return [];
      const occ = room.upcomingOccupancies?.[0];
      if (occ && showTenantNames) return [`${occ.tenantName} ${formatRelative(occ.startDate)} →`];
      if (occ) return [`Upcoming ${formatRelative(occ.startDate)} →`];
      return ["Upcoming"];
    }
    return [];
  }
  return [];
}

// A status line that merely repeats a tenant name already shown as the primary
// label is redundant (`null` — drop it). One that *leads* with the name but
// carries more — `"ClearMinds → in 12d"` — keeps its tail, so the lease end
// date still reaches the plan. Anything else is returned untouched.
// The joined form is tried first: it is the longest candidate, so a
// multi-tenant line matches on the whole prefix rather than its first name.
function stripTenantPrefix(line: string, tenantLines: string[], tenantNamesJoined: string): string | null {
  const candidates = tenantNamesJoined ? [tenantNamesJoined, ...tenantLines] : tenantLines;
  for (const name of candidates) {
    if (!name) continue;
    if (line === name) return null;
    if (line.startsWith(`${name} `)) return line.slice(name.length).trim();
  }
  return line;
}

function polygonBBoxWidth(points: number[][]): number {
  if (points.length === 0) return 0;
  let minX = Infinity, maxX = -Infinity;
  for (const p of points) {
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
  }
  return maxX - minX;
}

// Estimate text width (SVG has no measureText — approximate at ~0.6em per char)
function fitFontSize(text: string, maxWidth: number, baseFontSize: number, minFontSize: number): number {
  const charWidthRatio = 0.6;
  const estimatedWidth = text.length * baseFontSize * charWidthRatio;
  if (estimatedWidth <= maxWidth) return baseFontSize;
  const scaled = maxWidth / (text.length * charWidthRatio);
  return Math.max(minFontSize, Math.round(scaled * 10) / 10);
}

export function FloorplanRoomOverlay({
  rooms,
  selectedRoomId,
  onRoomClick,
  showManagementDetails = false,
  showTenantNames = true,
  relatedRoomIds,
  // When set, render only the polygons (clickable backdrop) or only the
  // text labels — every one of them: primary label, room number and status
  // lines. Defaults render both — kept for any caller that doesn't need a
  // furniture-text-on-top z-order. The standard Viewer mounts this component
  // twice: polygonsOnly under furniture, labelsOnly on top.
  mode = "both",
}: FloorplanRoomOverlayProps & { mode?: "both" | "polygonsOnly" | "labelsOnly" }) {
  const renderPolygons = mode !== "labelsOnly";
  const renderLabels = mode !== "polygonsOnly";
  // #416: the geometry of each merged room (one with folded-in parts), computed
  // once per rooms array rather than on every render: one fill path over the
  // parts plus bridges across the wall gaps, the closed outer outline, and the
  // label point / width of the whole group.
  const mergedShapes = useMemo(() => {
    const shapes = new Map<number, { fill: string; outline: string; labelPoint: [number, number]; width: number }>();
    for (const room of rooms) {
      if (!room.mergedPartIds?.length || !room.polygon?.length) continue;
      const bridges = mergedRoomBridges(room.polygon);
      shapes.set(room.id, {
        fill: mergedFillPath(room.polygon, bridges),
        outline: mergedOutlineSegments(room.polygon, undefined, bridges)
          .map(([p, q]) => `M${p[0]},${p[1]}L${q[0]},${q[1]}`)
          .join(""),
        labelPoint: mergedLabelPoint(room.polygon),
        width: polygonBBoxWidth(room.polygon.flat()),
      });
    }
    return shapes;
  }, [rooms]);
  // #428: rooms rented together with the selection, drawn with a dashed stroke (R17 — unset leaves the
  // markup byte-identical, since `isRelated` is then always false).
  const related = useMemo(() => new Set(relatedRoomIds ?? []), [relatedRoomIds]);
  return (
    <g>
      {rooms.map((room) => {
        // Skip rooms with no polygon data
        if (!room.polygon || room.polygon.length === 0 || room.polygon[0]?.length === 0) return null;

        const color = getRoomColor(room, showManagementDetails);
        const isSelected = room.id === selectedRoomId;
        const isRelated = !isSelected && related.has(room.id);
        const merged = mergedShapes.get(room.id);
        const statusLines = getStatusLines(room, showManagementDetails, showTenantNames);

        // Use labelPos if provided, otherwise compute centroid from first polygon.
        // A merged room centres its one label on the whole group instead.
        const firstPoly = room.polygon[0];
        const [labelX, rawLabelY] = merged
          ? merged.labelPoint
          : room.labelPos ?? polygonCentroid(firstPoly ?? []);

        // Display settings (defaults: all true)
        const showName = room.display?.showName !== false;
        const showStatus = room.display?.showStatus !== false;

        // Build label lines: primary (big, possibly multi-line), room
        // number (small), status lines (small). Label override takes
        // priority, then all tenant names (one per line), description,
        // room name.
        // Hiding names collapses this to [], which drops the label through to
        // `room.description || room.name` — the same branch an unoccupied room
        // already takes. That is what leaves a meeting room's name intact
        // while an office falls back to its number.
        const tenantLines = showTenantNames ? tenantNameLines(room) : [];
        const tenantNamesJoined = tenantLines.join(", ");
        const primaryLines: string[] = room.display?.label
          ? [room.display.label]
          : tenantLines.length > 0
            ? tenantLines
            : [room.description || room.name];
        const showRoomNum = showName && primaryLines[0] !== room.name;
        const newIcon = room.isNewMember ? " 🎉" : room.isNewNeighbour ? " ✨" : "";

        // Strip the tenant name from any status line that leads with a name we
        // already render as the primary label, rather than dropping the line:
        // the "ending" line is `"<tenant> → <date>"`, so a prefix match drops
        // the whole line and the lease end date never renders. A line that is
        // *only* the name (the "occupied" form) carries nothing else and is
        // dropped as before.
        const filteredStatus = (showStatus && tenantLines.length > 0)
          ? statusLines
              .map(l => stripTenantPrefix(l, tenantLines, tenantNamesJoined))
              .filter((l): l is string => l !== null)
          : statusLines;

        const visibleStatus = showStatus ? filteredStatus : [];

        // Compute room width to cap text size. Use the longest primary
        // line so a multi-name tenant doesn't shrink the whole label
        // because the joined form would have been too long.
        const roomWidth = merged ? merged.width : polygonBBoxWidth(firstPoly ?? []);
        const padding = 10; // px padding on each side
        const maxTextWidth = Math.max(roomWidth - padding * 2, 20);
        const longestPrimary = primaryLines.reduce((a, b) => (b.length > a.length ? b : a), "") + newIcon;
        const primaryFontSize = showName ? fitFontSize(longestPrimary, maxTextWidth, 12, 5) : 12;
        const smallFontSize = Math.min(8, primaryFontSize * 0.7);

        // Count total lines for vertical centering
        const primaryLineCount = showName ? primaryLines.length : 0;
        const totalLines = primaryLineCount + (showRoomNum ? 1 : 0) + visibleStatus.length;
        const lineHeight = primaryFontSize + 2;
        const labelY = rawLabelY - ((totalLines - 1) * lineHeight) / 2;

        return (
          <g
            key={room.id}
            style={{ cursor: onRoomClick ? "pointer" : "default" }}
            onClick={() => onRoomClick?.(room)}
          >
            {renderPolygons && !merged && room.polygon.map((poly, polyIdx) => {
              const pointsStr = poly.map((p) => `${p[0]},${p[1]}`).join(" ");
              return (
                <polygon
                  key={polyIdx}
                  points={pointsStr}
                  fill={color}
                  fillOpacity={isSelected ? 0.3 : 0.15}
                  stroke={color}
                  strokeWidth={isSelected || isRelated ? 3 : 2}
                  strokeDasharray={isRelated ? "6 4" : undefined}
                  strokeOpacity={0.9}
                />
              );
            })}
            {renderPolygons && merged && (
              <path
                d={merged.fill}
                fill={color}
                fillRule="nonzero"
                fillOpacity={isSelected ? 0.3 : 0.15}
                stroke="none"
              />
            )}
            {renderPolygons && merged && (
              <path
                d={merged.outline}
                fill="none"
                stroke={color}
                strokeWidth={isSelected || isRelated ? 3 : 2}
                strokeDasharray={isRelated ? "6 4" : undefined}
                strokeOpacity={0.9}
                strokeLinecap="round"
              />
            )}

            {/* Primary label — one tspan per line so a tenant with
                multiple display_names (e.g. "clearminds" + "Nodus")
                stacks vertically instead of running off the polygon
                as one comma-joined string. */}
            {renderLabels && showName && primaryLines.length > 0 && primaryLines[0] && (
              <text
                x={labelX}
                y={labelY}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={primaryFontSize}
                fontWeight={700}
                fill={color}
                style={{ pointerEvents: "none" }}
              >
                {primaryLines.map((line, li) => (
                  <tspan key={li} x={labelX} dy={li === 0 ? 0 : primaryFontSize + 2}>
                    {line}{li === primaryLines.length - 1 ? newIcon : ""}
                  </tspan>
                ))}
              </text>
            )}

            {/* Room number (always small, below primary) */}
            {renderLabels && showRoomNum && (
              <text
                x={labelX}
                y={labelY + primaryFontSize * primaryLineCount + 2}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={smallFontSize}
                fill={color}
                opacity={0.6}
                style={{ pointerEvents: "none" }}
              >
                {room.name}
              </text>
            )}

            {/* Status lines */}
            {renderLabels && visibleStatus.length > 0 && (
              <text
                x={labelX}
                y={labelY + (showRoomNum
                  ? primaryFontSize * primaryLineCount + smallFontSize + 4
                  : showName ? primaryFontSize * primaryLineCount + 2 : 0)}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={smallFontSize}
                fontWeight={500}
                fill={color}
                style={{ pointerEvents: "none" }}
              >
                {visibleStatus.map((line, li) => (
                  <tspan key={li} x={labelX} dy={li === 0 ? 0 : smallFontSize + 3} opacity={li === 0 ? 1 : 0.7}>
                    {line}
                  </tspan>
                ))}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}
